import React, { useEffect, useRef } from 'react';
import { Redirect, Route as ReactRoute, Switch, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Checkbox, alpha, FormControlLabel, lighten, TextField, Typography, CircularProgress } from '@material-ui/core';
import { ConfigFieldControl, useTimeout } from '@sierralabs/core-ui';
import { WorkflowPluginRoutes } from '@sierralabs/workflow-ui';
import ComponentPicker from '../../common/pickers/ComponentPicker';
import SitePermissionsPicker from '../../common/pickers/SitePermissionsPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteRouteAccess } from '../../hooks/site-route/useSiteRouteAccess';
import { useSiteRouteComponents } from '../../hooks/site-route/useSiteRouteComponents';
import { useSiteContext } from '../../SiteContext';
import { SlotRenderer } from '../renderers';
import { SiteComponent } from '../SiteComponentPlugin';
import { useState } from 'react';
import SiteNoutFoundPage from '../../SiteNotFoundPage';
import { Button } from '@material-ui/core';
import EditRouteDialog from '../../dialogs/EditRouteDialog';
const StyledDiv = styled.div `
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1299; /* MUI Dialog has z-index = 1300 */
  transform: scale(1);
  &::before {
    content: '${props => props.path}';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    padding: 8px 12px;
    background: #3f3f3fcc;
    color: #fff;
    z-index: 1299; /* MUI Dialog has z-index = 1300 */
  }
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: solid 2px #3f3f3f;
    z-index: 1299; /* MUI Dialog has z-index = 1300 */
    pointer-events: none;
  }
  &.route-hover {
    border-color: ${props => props.theme.palette.primary.main};
    &::before {
      background: ${props => props.theme.palette.primary.main}cc;
    }
  }
  .component-hover {
    position: relative;
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      background: ${props => alpha(props.theme.palette.primary.main, 0.1)};
      border: solid 2px
        ${props => lighten(props.theme.palette.primary.main, 0.75)};
    }
  }
`;
const StyledSpinnerContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;
export const SiteRootRoutes = props => {
    const { loading, children } = props;
    const history = useHistory();
    const mountRef = useRef(true);
    const [mount, setMount] = useState(true);
    const { editPath, loadingSite, loadingSiteComponents } = useSiteContext();
    const routeComponents = useSiteRouteComponents();
    // redirect to edit path if editPath changes after mount
    useEffect(() => {
        if (mountRef.current) {
            mountRef.current = false;
            setMount(false);
        }
        else if (editPath) {
            history.push(editPath);
        }
    }, [editPath, history]);
    if (mount || loadingSite || loadingSiteComponents) {
        return typeof loading !== 'undefined' ? (loading) : (React.createElement(StyledSpinnerContainer, null,
            React.createElement(CircularProgress, { size: 64 })));
    }
    // wait for routes to finish loading
    if (routeComponents.length === 0) {
        return null;
    }
    return (React.createElement(Switch, null,
        routeComponents
            .filter(c => c.depth === 0)
            .map(component => {
            var _a, _b;
            // console.log('route map', component);
            // need to redeclare ReactRoute here even though its used in the
            // SiteRoute component because of the <Switch>
            return (React.createElement(ReactRoute, { key: component.id, exact: (_a = component.props) === null || _a === void 0 ? void 0 : _a.exact, path: (_b = component.props) === null || _b === void 0 ? void 0 : _b.path },
                React.createElement(SiteRoute, { root: true, component: component })));
        }),
        children,
        React.createElement(WorkflowPluginRoutes, null),
        React.createElement(ReactRoute, { path: '*', component: SiteNoutFoundPage })));
};
export const SiteRoute = props => {
    var _a, _b, _c, _d, _e;
    const { root, children } = props;
    const component = useSubscribeComponentChanges(SiteRoutePlugin, props);
    const { editing, isHover, hoverProps, hoverComponentRef, siteComponentMap, setHoverComponent, setSelectedComponent } = useSiteContext();
    const classes = ['site-route'];
    if (isHover(component)) {
        classes.push('route-hover');
    }
    const { isInPath, hasAccess, redirectPath } = useSiteRouteAccess(((_a = component.props) === null || _a === void 0 ? void 0 : _a.path) || '', (_b = component.props) === null || _b === void 0 ? void 0 : _b.permissions);
    // console.log('route', component, props);
    // no redirects if in edit mode
    if (!editing && !hasAccess && isInPath) {
        return React.createElement(Redirect, { to: redirectPath });
    }
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteRoutePlugin }, props),
        React.createElement(ReactRoute, { exact: (_c = component.props) === null || _c === void 0 ? void 0 : _c.exact, path: (_d = component.props) === null || _d === void 0 ? void 0 : _d.path }, editing ? (React.createElement(StyledDiv, Object.assign({ className: classes.join(' '), path: `Route: ${(_e = component.props) === null || _e === void 0 ? void 0 : _e.path}` || 'Route' }, hoverProps(component), { onClick: event => {
                // Root route components handle component selection when editing
                if (!root || !editing)
                    return;
                const componentId = findParentComponentId(event.target);
                if (componentId) {
                    const component = siteComponentMap[componentId];
                    setSelectedComponent(component);
                }
            }, onMouseOver: event => {
                var _a;
                // Root route component handles hover events on all nested
                // components
                if (!root || !editing)
                    return;
                // console.log('onMouseOver', event.target);
                const componentId = findParentComponentId(event.target);
                if (componentId &&
                    componentId !== ((_a = hoverComponentRef.current) === null || _a === void 0 ? void 0 : _a.id)) {
                    const component = siteComponentMap[componentId];
                    setHoverComponent(component);
                }
            }, onMouseOut: event => {
                // console.log('onMouseOut', event.target);
                editing && setHoverComponent(undefined);
            } }),
            React.createElement(SlotRenderer, { component: component, slot: 'route' }))) : (React.createElement(SlotRenderer, { component: component, slot: 'route' }))),
        children));
};
const findParentComponentId = (element) => {
    // console.log('find element', element);
    if (element.dataset.componentId) {
        return element.dataset.componentId;
    }
    else if (element.parentElement) {
        return findParentComponentId(element.parentElement);
    }
};
export const SiteRouteConfigurator = props => {
    var _a, _b, _c, _d, _e;
    const [openEditRouteDialog, setOpenEditRouteDialog] = useState(false);
    const [debounceSet] = useTimeout();
    const { setEditPath } = useSiteContext();
    const { component, update, updateSlot } = useConfigComponent(props.component);
    const slotComponent = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find(c => c.slot === 'route');
    const parentPath = component.parent
        ? getRoutePath(component.parent) || ''
        : '';
    const path = ((_c = (_b = component.props) === null || _b === void 0 ? void 0 : _b.path) === null || _c === void 0 ? void 0 : _c.substring(parentPath.length)) || '';
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', label: 'Name', value: component.name || '', onChange: event => {
                    update('name', event.target.value);
                }, InputProps: { disableUnderline: true } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Typography, { variant: 'caption', color: 'textSecondary' }, "Path:"),
            React.createElement(Button, { style: { textTransform: 'lowercase' }, variant: 'text', color: 'primary', size: 'small', onClick: () => setOpenEditRouteDialog(true) }, path ? `${parentPath}${path}` : 'Edit Path')),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Exact Path', control: React.createElement(Checkbox, { color: 'primary', size: 'small', className: 'checkbox', checked: Boolean((_d = component.props) === null || _d === void 0 ? void 0 : _d.exact), onChange: () => { var _a; return update('props.exact', !((_a = component.props) === null || _a === void 0 ? void 0 : _a.exact)); } }) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(SitePermissionsPicker, { value: (_e = component.props) === null || _e === void 0 ? void 0 : _e.permissions, textFieldProps: {
                    size: 'small',
                    label: 'Select Permission Access',
                    InputProps: { disableUnderline: true }
                }, onChange: permissions => {
                    update('props.permissions', permissions.map(permission => permission.name));
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ComponentPicker, { label: 'Component', value: (slotComponent === null || slotComponent === void 0 ? void 0 : slotComponent.type) || '', onTypeChange: type => {
                    updateSlot('route', type);
                } })),
        openEditRouteDialog && (React.createElement(EditRouteDialog, { open: openEditRouteDialog, component: props.component, currentPath: path, parentPath: parentPath, onCancel: () => setOpenEditRouteDialog(false), onChange: path => {
                update('props.path', path);
                setOpenEditRouteDialog(false);
                // update the iframe after components are updated
                debounceSet(() => setEditPath(path), 500);
            } }))));
};
export const SiteRoutePlugin = {
    name: 'Site Route',
    type: 'route',
    component: SiteRoute,
    configurator: SiteRouteConfigurator,
    initConfig: (component, { selectedComponentRef }) => {
        // get route path to selected component
        if (selectedComponentRef.current) {
            // createComponentWithParent(rootRouteComponent);
            const path = getRoutePath(selectedComponentRef.current);
            if (!component.props)
                component.props = {};
            component.props.path = component.parent ? `${path}/` : path; // add '/' on child route on default config
        }
        return component;
    }
};
// const createComponentWithParent = (
//   component: Component<any> & { parent?: Component<any> },
//   parent?: Component<any>
// ) => {
//   if (parent) {
//     component.parent = parent;
//   }
//   if (component.children) {
//     for (const child of component.children) {
//       createComponentWithParent(child, component);
//     }
//   }
//   return component;
// };
const getRoutePath = (component, path) => {
    var _a;
    if (!path)
        path = [];
    if (component.type === 'route' && ((_a = component.props) === null || _a === void 0 ? void 0 : _a.path)) {
        path.unshift(component.props.path);
    }
    if (component.parent) {
        getRoutePath(component.parent, path);
    }
    return path[path.length - 1]; // get the last path on the array of path
};
//# sourceMappingURL=SiteRoutePlugin.js.map